// RC10f · Admin → Fleet Settings → Unit Types CRUD page (Correction B).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { unitTypeApi } from "@/lib/entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { RoleGate } from "@/components/RoleGate";
import { Plus, Archive, RotateCcw, ArrowUp, ArrowDown, Save } from "lucide-react";

export default function UnitTypesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await unitTypeApi.list();
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const code = newCode.trim().toLowerCase().replace(/\s+/g, "_");
    const label = newLabel.trim();
    if (!code || !label) { toast.error("Both code and label are required."); return; }
    try {
      await unitTypeApi.create({ code, label, display_order: 500 });
      toast.success("Unit type created.");
      setShowAdd(false); setNewCode(""); setNewLabel("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed."); }
  };

  const rename = async (id, newLabel) => {
    try { await unitTypeApi.patch(id, { label: newLabel }); load(); toast.success("Renamed."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Rename failed."); }
  };

  const move = async (id, delta) => {
    const row = rows.find(r => r.id === id);
    if (!row) return;
    const target = (row.display_order || 0) + delta * 10;
    try { await unitTypeApi.patch(id, { display_order: target }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reorder failed."); }
  };

  const archive = async (id) => {
    try { await unitTypeApi.archive(id); load(); toast.success("Archived."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Archive failed."); }
  };
  const reactivate = async (id) => {
    try { await unitTypeApi.reactivate(id); load(); toast.success("Reactivated."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reactivate failed."); }
  };

  return (
    <RoleGate roles={["super_admin"]}>
      <PageShell>
        <PageHeader
          title="Unit Types"
          subtitle="Authoritative catalog driving the New / Edit Asset Type selectors."
          actions={<button data-testid="ut-add-open" onClick={() => setShowAdd((v) => !v)}
                     className="inline-flex items-center gap-1 px-3 py-1.5 bg-coach-blue text-white rounded-sm text-sm">
                     <Plus size={14}/>Add type
                   </button>}
        />
        {showAdd && (
          <div className="mb-4 p-3 border border-coach-silver bg-road-white rounded-sm">
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs">
                Code (slug — immutable after save)
                <input data-testid="ut-add-code" value={newCode}
                       onChange={(e) => setNewCode(e.target.value)}
                       className="w-full mt-0.5 border border-coach-silver rounded-sm px-2.5 py-1.5 text-sm font-mono"/>
              </label>
              <label className="text-xs">
                Label (shown to users)
                <input data-testid="ut-add-label" value={newLabel}
                       onChange={(e) => setNewLabel(e.target.value)}
                       className="w-full mt-0.5 border border-coach-silver rounded-sm px-2.5 py-1.5 text-sm"/>
              </label>
            </div>
            <div className="mt-2 flex gap-2">
              <button data-testid="ut-add-save" onClick={create}
                      className="px-3 py-1.5 bg-coach-blue text-white rounded-sm text-sm inline-flex items-center gap-1">
                <Save size={14}/>Save
              </button>
              <button onClick={() => setShowAdd(false)}
                      className="px-3 py-1.5 border border-coach-silver rounded-sm text-sm">Cancel</button>
            </div>
          </div>
        )}
        <div className="bg-white border border-coach-silver rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-chrome-mist">
              <tr className="text-left text-xs uppercase tracking-wider text-steel">
                <th className="px-3 py-2 w-24">Order</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2 w-28">State</th>
                <th className="px-3 py-2 w-40 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-3 py-6 text-steel text-center">Loading…</td></tr>}
              {!loading && rows.map((r) => (
                <tr key={r.id} data-testid={`ut-row-${r.code}`}
                    className={`border-t border-coach-silver/40 ${r.is_archived ? "bg-chrome-mist/40 text-steel" : ""}`}>
                  <td className="px-3 py-2 font-mono">{r.display_order}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`ut-label-${r.code}`}
                      defaultValue={r.label}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.label) rename(r.id, v); }}
                      className="w-full border-0 bg-transparent focus:bg-white focus:border focus:border-coach-silver rounded-sm px-2 py-1"
                    />
                  </td>
                  <td className="px-3 py-2 text-xs uppercase tracking-wider">
                    {r.is_archived ? "Archived" : "Active"}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button title="Move up" data-testid={`ut-up-${r.code}`} onClick={() => move(r.id, -1)}
                            className="inline-flex items-center p-1 border border-coach-silver rounded-sm"><ArrowUp size={12}/></button>
                    <button title="Move down" data-testid={`ut-down-${r.code}`} onClick={() => move(r.id, +1)}
                            className="inline-flex items-center p-1 border border-coach-silver rounded-sm"><ArrowDown size={12}/></button>
                    {!r.is_archived ? (
                      <button title="Archive" data-testid={`ut-archive-${r.code}`} onClick={() => archive(r.id)}
                              className="inline-flex items-center p-1 border border-maintenance-orange text-maintenance-orange rounded-sm"><Archive size={12}/></button>
                    ) : (
                      <button title="Reactivate" data-testid={`ut-reactivate-${r.code}`} onClick={() => reactivate(r.id)}
                              className="inline-flex items-center p-1 border border-coach-blue text-coach-blue rounded-sm"><RotateCcw size={12}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-steel">
          <strong>Note:</strong> the <code>code</code> is the stable key stored on each Asset.
          Renames update the label everywhere. Archive hides the type from new selections but
          historical asset values remain intact.
        </p>
      </PageShell>
    </RoleGate>
  );
}
